import type { CalendarId } from 'owa-graph-schema';
import type { ChannelOption } from 'owa-calendar-channel-picker/lib/store/schema/CalendarChannelPickerInterfaces';
import type { CharmInfo } from 'owa-calendar-charms-store';
import type { CharmSelectionType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import type SkypeTeamsPropertiesData from 'owa-calendar-types/lib/types/SkypeTeamsPropertiesData';
import type { ClientAttachmentId, ClientFolderId } from 'owa-client-ids';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import type { OnlineMeetingProviderType } from 'owa-online-meeting/lib/utils/OnlineMeetingProviderType';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type { CollabSpaceData } from 'owa-calendar-types';

import { action } from 'satcheljs';

/**
 * Action called when subject is changed through the UI
 */
export let onSubjectChanged = action(
    'onSubjectChanged',
    (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        subject: string,
        formId: string
    ) => ({
        subject: subject,
        formId: formId,
    })
);

/**
 * Action called when the charm is changed through the UI
 * TODO: WI:28099 To be removed when full compose moves from using isCharmExplicitySet to CharmSelectionType
 */
export let onCharmChanged = action('onCharmChanged', (icon: CharmInfo, formId: string) => ({
    iconId: icon.IconId,
    formId: formId,
}));

/**
 * Action called when the charm is changed through the UI
 */
export let onCharmAndSelectionTypeChanged = action(
    'onCharmAndSelectionTypeChanged',
    (formId: string, icon: CharmInfo, charmSelection: CharmSelectionType) => ({
        formId: formId,
        iconId: icon.IconId,
        charmSelection: charmSelection,
    })
);

/**
 * Action called to change the CalendarId
 */
export let onCalendarIdChanged = action(
    'onCalendarIdChanged ',
    (CalendarId: CalendarId, formId: string) => ({
        CalendarId,
        formId,
    })
);

/**
 * Action called when account change is detected
 */
export let onAccountChanged = action(
    'onAccountChanged',
    (folderId: ClientFolderId, formId: string) => ({
        folderId,
        formId,
    })
);

/**
 * Action called to change the ParentFolderId
 */
export let onParentFolderIdChanged = action(
    'onParentFolderIdChanged',
    (ParentFolderId: ClientFolderId, formId: string) => ({
        ParentFolderId: ParentFolderId,
        formId: formId,
    })
);
/**
 *
/**
 * Action called when sensitivity value is toggled
 */
export let onSensitivityToggled = action('onSensitivityToggled', (formId: string) => ({
    formId: formId,
}));

/**
 * Action called to update ReminderMinutesBeforeStart
 */
export let onReminderChanged = action(
    'onReminderChanged',
    (reminderMinutesBeforeStart: number, formId: string) => ({
        reminderMinutesBeforeStart: reminderMinutesBeforeStart,
        formId: formId,
    })
);

/**
 * Action called when draft flag is updated.
 */
export let onIsDraftChanged = action(
    'onIsDraftChanged',
    (isDraftEvent: boolean, formId: string) => ({
        isDraftEvent: isDraftEvent,
        formId: formId,
    })
);

/*
 * Action triggered when the allowForwarding is changed in response options to update DoNotForwardMeeting
 */
export let onAllowForwardingChanged = action(
    'onAllowForwardingChanged',
    (allowForwardingState: boolean, formId: string) => ({
        DoNotForwardMeeting: !allowForwardingState,
        formId: formId,
    })
);

/**
 * Action triggered when the onRequestResponse is changed in response options to update IsResponseRequested
 */
export let onRequestResponseChanged = action(
    'onRequestResponseChanged',
    (requestResponseState: boolean, formId: string) => ({
        IsResponseRequested: requestResponseState,
        formId: formId,
    })
);

/*
 * Action triggered when onHideAttendeeList is changed in response options to update HideAttendees
 */
export let onHideAttendeeListChanged = action(
    'onHideAttendeeListChanged',
    (hideAttendeeListState: boolean, formId: string) => ({
        HideAttendees: hideAttendeeListState,
        formId: formId,
    })
);

/**
 * Action triggered when the Add skype meeting button is clicked to change its flag.
 */
export let onIsOnlineMeetingChanged = action(
    'onIsOnlineMeetingChanged',
    (
        IsOnlineMeeting: boolean,
        onlineMeetingProvider: OnlineMeetingProviderType,
        isOnlineMeetingExplicitlySet: boolean,
        wasOnlineMeetingEverAutoSet: boolean,
        formId: string
    ) => ({
        IsOnlineMeeting,
        onlineMeetingProvider,
        isOnlineMeetingExplicitlySet,
        wasOnlineMeetingEverAutoSet: wasOnlineMeetingEverAutoSet,
        formId: formId,
    })
);

/**
 * Action called to update RequiredAttendees
 */
export let onRequiredAttendeesChanged = action(
    'onRequiredAttendeesChanged',
    (requiredAttendees: AttendeeType[], formId: string) => ({
        requiredAttendees: requiredAttendees,
        formId: formId,
    })
);

/*
 * Action called to update OptionalAttendees
 */
export let onOptionalAttendeesChanged = action(
    'onOptionalAttendeesChanged',
    (optionalAttendees: AttendeeType[], formId: string) => ({
        optionalAttendees: optionalAttendees,
        formId: formId,
    })
);

/*
 * Action called to update Resources
 */
export let onResourcesChanged = action(
    'onResourcesChanged',
    (resources: AttendeeType[], formId: string) => ({
        resources: resources,
        formId: formId,
    })
);

/**
 * Action called when an attachment is created
 */
export let onAttachmentCreated = action(
    'onAttachmentCreated',
    (attachmentId: ClientAttachmentId, attachment: AttachmentType, formId: string) => ({
        attachmentId: attachmentId,
        attachment: attachment,
        formId: formId,
    })
);

/**
 * Action called when an attachment is deleted
 */
export let onAttachmentDeleted = action(
    'onAttachmentDeleted',
    (attachmentId: ClientAttachmentId, attachment: AttachmentType, formId: string) => ({
        attachmentId: attachmentId,
        attachment: attachment,
        formId: formId,
    })
);

/**
 * Action called when an all attachments are cleared
 */
export let onAttachmentsCleared = action('onAttachmentsCleared', (formId: string) => ({
    formId,
}));

/**
 * Action called to change categories property
 */
export let onCategoriesChanged = action(
    'onCategoriesChanged',
    (Categories: string[], formId: string) => ({
        Categories,
        formId,
    })
);

/**
 * Action called to change the dates from the date selector
 */
export let onDateTimesChanged = action(
    'onDateTimesChanged',
    (datesSelected: OwaDateRange, formId: string) => ({
        datesSelected,
        formId,
    })
);

/**
 * Action called to change the all day property
 */
export let onAllDayChanged = action(
    'onAllDayChanged',
    (isAllDayEvent: boolean, formId: string) => ({
        isAllDayEvent,
        formId,
    })
);
/**
 * Action called to update the automatic room booking flag
 */
export let onRoomRequestedChanged = action(
    'onRoomRequestedChanged',
    (isRoomRequested: boolean, formId: string) => ({
        isRoomRequested,
        formId,
    })
);

/**
 * Action called to update the skype/teams properties
 */
export let onSkypeTeamsPropertiesChanged = action(
    'onSkypeTeamsPropertiesChanged',
    (skypeTeamsProperties: SkypeTeamsPropertiesData, formId: string) => ({
        skypeTeamsProperties,
        formId,
    })
);

/**
 * Action called when a channel is selected
 */
export let onChannelChanged = action(
    'onChannelChanged ',
    (channelOption: ChannelOption, formId: string) => ({
        channelOption,
        formId,
    })
);

/**
 * Action called to update body value of event
 */
export let onUpdateBodyValueOfEvent = action(
    'onUpdateBodyValueOfEvent ',
    (Value: string, HasBlockedImages: boolean, formId: string) => ({
        Value: Value,
        HasBlockedImages: HasBlockedImages,
        formId: formId,
    })
);

/**
 * Action called to update the collaborative space details
 */
export let onCollabSpaceUpdated = action(
    'onCollabSpaceUpdated',
    (collabSpace: CollabSpaceData, formId: string) => ({
        collabSpace,
        formId,
    })
);
